import { useState, useRef, useMemo, useEffect } from 'react';
import { Menu, MenuItem } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useFlags } from 'launchdarkly-react-client-sdk';
import StatusPill from '../../../Shared/Components/StatusPill';
import UpdateStatusButtonModal from './UpdateStatusButtonModal';
import { PROCESSED_TIMESHEET_STATUS_LABELS } from '../../constants';
import useAdminAuthContext from '../../hooks/useAdminAuthContext';
import { useToast } from '../../Contexts/ToastContext';
import { isPtsStatusReadOnly } from '../../Configurations/processedTimesheetsConfig';
import { useProcessedTimesheetContext } from '../../Contexts/ProcessedTimesheetContext';
import useGetProcessedTimesheetsStatuses from '../../hooks/ProcessedTimesheets/useGetProcessedTimesheetsStatuses';

const UpdateStatusButton = ({
    id,
    status: initialStatus,
    needsReviewStatus: initialNeedsReviewStatus,
    variant = 'lt-admin-pill',
    clinicianName = '',
    lastModified,
    isCorrection = false,
}) => {
    const { isTimesheetAdminUser, isTimesheetSystemAdminUser } = useAdminAuthContext();
    const statusButtonRef = useRef(null);
    const [menuOpen, setMenuOpen] = useState(false);
    const [openConfirmationModal, setOpenConfirmationModal] = useState(false);
    const [currentStatus, setCurrentStatus] = useState(initialNeedsReviewStatus || initialStatus);
    const [newStatus, setNewStatus] = useState();
    const { showSuccessToast, showErrorToast } = useToast();
    const { hasRecentChanges } = useProcessedTimesheetContext();
    const { ltoeApiTimesheetEnableEditApprovedTimesheet } = useFlags();
    const { data: availableStatusesData } = useGetProcessedTimesheetsStatuses({
        onError: (error) => {
            console.error(error);
            showErrorToast(
                'Failed to load available statuses. You will not be able to update status of a processed timesheet. Please refresh the page to try again.'
            );
        },
    });
    const availableStatuses = availableStatusesData?.statusTransitions;
    const availableNeedsReviewStatuses = availableStatusesData?.needsReviewStatusTransitions;

    useEffect(() => {
        setCurrentStatus(initialNeedsReviewStatus || initialStatus);
    }, [initialNeedsReviewStatus, initialStatus]);

    const toggleChangeStatusMenu = () => {
        setMenuOpen((prev) => !prev);
    };
    const closeMenu = () => {
        setMenuOpen(false);
    };
    const handleChangeStatus = (newStatusProp) => {
        setNewStatus(newStatusProp);
        closeMenu();
        setOpenConfirmationModal(true);
    };

    const handleChangeStatusSuccess = (status) => {
        setCurrentStatus(status);
        setNewStatus(undefined);
        setOpenConfirmationModal(false);
        showSuccessToast(
            `${
                clinicianName ? `${clinicianName}'s ` : ''
            }processed timesheet status was successfully updated.`
        );
    };

    const filteredStatusOptions = useMemo(() => {
        const returnedStatuses = [];
        if (availableStatuses && availableStatuses[currentStatus]) {
            returnedStatuses.push(
                availableStatuses[currentStatus]?.statuses,
                availableStatuses[currentStatus]?.needsReviewStatuses
            );
        } else if (availableNeedsReviewStatuses && availableNeedsReviewStatuses[currentStatus]) {
            returnedStatuses.push(
                availableNeedsReviewStatuses[currentStatus]?.statuses,
                availableNeedsReviewStatuses[currentStatus]?.needsReviewStatuses
            );
        }
        if (isCorrection) {
            if (currentStatus !== 'InvoiceReview') {
                return returnedStatuses.flat().filter((status) => status !== 'Resolved');
            }
        }
        return returnedStatuses.flat().filter((status) => status !== 'InvoiceReview');
    }, [availableStatusesData]);

    const hasEditPermission =
        ltoeApiTimesheetEnableEditApprovedTimesheet &&
        (isTimesheetAdminUser || isTimesheetSystemAdminUser) &&
        !isPtsStatusReadOnly(initialStatus) &&
        filteredStatusOptions.length > 0 &&
        !hasRecentChanges;

    return (
        <>
            <StatusPill
                status={currentStatus}
                variant={variant}
                data-testid="current-status-pill"
                content={
                    <>
                        {PROCESSED_TIMESHEET_STATUS_LABELS[currentStatus]}
                        {hasEditPermission && (
                            <KeyboardArrowDownIcon
                                sx={{
                                    ml: 'auto',
                                    transition: 'transform 0.3s',
                                    transform: menuOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                }}
                            />
                        )}
                    </>
                }
                title="Change status"
                size="small"
                sx={{ minWidth: 146 }}
                ref={statusButtonRef}
                onClick={hasEditPermission ? toggleChangeStatusMenu : undefined}
                disabled={!id || !currentStatus}
                aria-controls={menuOpen ? 'change-status-options' : undefined}
                aria-haspopup="true"
                aria-expanded={menuOpen ? 'true' : undefined}
            />
            <Menu
                id="change-status-options"
                data-testid="change-status-options"
                slotProps={{ list: { 'aria-labelledby': 'Change status options' } }}
                anchorEl={statusButtonRef.current}
                open={menuOpen}
                onClose={closeMenu}
            >
                {filteredStatusOptions.map((state) => (
                    <MenuItem key={state} onClick={() => handleChangeStatus(state)} disableRipple>
                        <StatusPill status={state} sx={{ minWidth: 146 }} />
                    </MenuItem>
                ))}
            </Menu>
            {openConfirmationModal && (
                <UpdateStatusButtonModal
                    id={id}
                    onClose={() => setOpenConfirmationModal(false)}
                    currentStatus={currentStatus}
                    newStatus={newStatus}
                    setNewStatus={setNewStatus}
                    onSuccess={handleChangeStatusSuccess}
                    statusOptions={filteredStatusOptions}
                    lastModified={lastModified}
                />
            )}
        </>
    );
};

export default UpdateStatusButton;
